import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  useForm,
  useFieldArray,
  useWatch,
  type StringIndexedObject,
  type UseFormReturn,
} from "@kanda-libs/ks-component-ts";
import { useToast } from "@kanda-libs/ks-design-library";
import { actions, type Company } from "@kanda-libs/ks-frontend-services";
import { useAppDispatch } from "components/shared/App";
import useApiError from "hooks/useApiError";
import { selectors } from "store";
import {
  toConfigRows,
  validateConfigs,
  type EditLenderConfigFormValues,
  type LenderConfigRow,
} from "./helpers";

export interface EditLenderConfigHook {
  form: UseFormReturn<EditLenderConfigFormValues>;
  fields: { id: string; isNew: boolean }[];
  configs: LenderConfigRow[];
  addLender: () => void;
  deleteLender: (index: number) => void;
  onSubmit: (values: StringIndexedObject) => void;
  isSubmitting: boolean;
}

export default function useEditLenderConfig(
  handleClose: () => void,
): EditLenderConfigHook {
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useToast();
  const onApiError = useApiError("Unknown error updating lender configs");

  const company = useSelector(selectors.company.getEntity);
  const isSubmitting = useSelector(selectors.company.getIsSubmitting);

  const form = useForm<EditLenderConfigFormValues>({
    mode: "onBlur",
    defaultValues: { configs: [] },
  });
  const { control, reset } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "configs",
  });

  // Load the company's current lender configs into the form.
  useEffect(() => {
    reset({ configs: toConfigRows(company?.lender_configs) });
  }, [company?.lender_configs, reset]);

  const configs =
    (useWatch({ control, name: "configs" }) as LenderConfigRow[]) || [];

  const addLender = useCallback(
    () => append({ provider: "", xid: "", isNew: true }),
    [append],
  );

  const deleteLender = useCallback((index: number) => remove(index), [remove]);

  const onSubmit = useCallback(
    (values: StringIndexedObject) => {
      const { configs: rows } = values as EditLenderConfigFormValues;
      const { valid, error, lenderConfigs } = validateConfigs(rows);
      if (!valid) {
        showError(error || "Invalid lender configs");
        return;
      }
      const id = company?.cid;
      if (!company || !id) {
        showError("Company not found");
        return;
      }
      dispatch(
        actions.putCompany({
          body: { ...company, lender_configs: lenderConfigs } as Company,
          params: { id },
          onSuccess: () => {
            showSuccess("Lender configs updated");
            handleClose();
          },
          onError: (err) => onApiError(err),
        }),
      );
    },
    [company, dispatch, showSuccess, showError, onApiError, handleClose],
  );

  return {
    form,
    fields: fields as unknown as { id: string; isNew: boolean }[],
    configs,
    addLender,
    deleteLender,
    onSubmit,
    isSubmitting,
  };
}
